import nav from '@/containers/_nav_admin'
import { accessPolicyForPath } from './accessPolicy'
import { sidebarMenuItems } from './sidebarNavigation'

/**
 * 権限管理の画面で、権限1つ1つを「何ができるようになるか」で説明するための表と計算（2026-09-26）。
 *
 * ■ なぜ要るのか
 *
 * 以前の画面は権限を「自分の勤怠を利用」「利用機能の設定を確認」のような名前で、「承認」「その他」
 * 「システム管理」という分け方で並べていただけだった。利用者（契約企業の社長）から「ユーザー管理は
 * どの権限で使えるのか分からない」「出ていない機能をオン・オフできない」と言われた。
 * あとから足した権限4つは画面の表に無く、DB の名前のまま「その他」へ落ちていた。
 *
 * ■ 決め事
 *
 * - 名前と分け方は、背景と仕組みを説明したうえで Gemini と決めた
 *   （C:\biz\claude\権限管理の言葉_Geminiへの相談と答え_2026-09-26.md）。
 * - 「対象メニュー」は**手で書かない。**左メニューを出し分けている経路表（accessPolicy.js の POLICIES）
 *   から計算する。左メニューを直したときに、この画面の説明だけ古くなるのを防ぐ。
 * - 表に無い権限が来たら、試験が落ちる（tests/unit/utils/permissionGuideCoversEveryPermission.spec.js）。
 */

/** 見出し（Gemini と決めた分け方）。並びもこの順。 */
export const PERMISSION_GROUPS = [
  { key: 'self', title: '自分の業務（月次報告・随時報告）', note: '' },
  { key: 'approval', title: '申請の承認・状況確認', note: '' },
  { key: 'insight', title: '集計・経営分析', note: '' },
  { key: 'business', title: '業務管理（プロジェクト・取引先・請求）', note: '' },
  { key: 'organization', title: '組織・ユーザー・自社管理', note: '' },
  {
    key: 'systemAdminOnly',
    title: 'システム管理（社内管理者限定機能）',
    // どの権限グループに入れても、その社員が社内システム管理者の権限グループも持つときだけ効く（権限は合算される）。
    note: '以下の権限は「社内システム管理者」グループも併せて割り当てられている社員にのみ適用されます。'
  },
  {
    key: 'noEffect',
    title: 'その他（設定不要）',
    note: '現在お客様の画面では使用しない、または準備中の項目です。'
  }
]

/**
 * 権限ごとの名前・見出し・画面の中の操作。
 *
 * - operations：メニューは増えないが、画面の中でできるようになる操作（計算では出せないものだけ書く）。
 *   根拠は back の口の守り（C:\biz\claude\左メニューと権限の対応表_ソースから洗い出し_2026-09-26.md の 2-1）。
 *   「有休取得計画の全社表示」は、back がこの8つのどれかで全社の分を返している
 *   （ManagementInsightService.ACCOUNT_WIDE_PERMISSIONS）。
 * - onlyForRoles：この権限グループも割り当てられている社員にだけ効く。権限は割り当て中の全部の権限グループで
 *   合算される（back の AccessControlMapper.selectPermissionCodes）ので、ほかの権限グループに入れても、
 *   その社員がこの権限グループも持っていれば効く
 * - noEffect：会社の画面ではどこにも効かない
 */
const PAID_LEAVE_COMPANY_WIDE = '全社の有休取得計画の閲覧'

export const PERMISSION_GUIDE = {
  ATTENDANCE_USE_SELF: { name: '自分の勤怠・業務報告', group: 'self', operations: ['打刻'] },
  REQUEST_USE_SELF: {
    name: '自分の申請（有休・残業など）',
    group: 'self',
    operations: ['申請の提出・取り下げ']
  },
  EXPENSE_USE_SELF: { name: '自分の経費・通勤費精算', group: 'self' },
  APPROVAL_MANAGE: {
    name: '全社の申請承認・一覧操作',
    group: 'approval',
    operations: ['承認の取り消し', 'ダッシュボード（承認状況）の閲覧', PAID_LEAVE_COMPANY_WIDE]
  },
  /*
   * 2026-10-09：ダッシュボードの「承認滞留分析」を、この権限だけの人にも出すようにした
   * （自分宛ての承認待ちが1件以上あるとき）。できることの一覧に足す（語句は Gemini の DS-35）。
   */
  APPROVAL_USE_ASSIGNED: {
    name: '担当申請の承認',
    group: 'approval',
    operations: ['自分宛ての申請の承認・差し戻し', '担当宛ての承認滞留状況の閲覧']
  },
  MONTHLY_AGGREGATION_VIEW: {
    name: '月次・プロジェクト集計の閲覧',
    group: 'insight',
    // 一覧を開ける人は、一覧の［承認取消］も押せる（旧 clerk がそうしていた。束5、2026-09-30）。
    // back の経路表（/submission/revocation_of_approval）と同じ。
    operations: ['ダッシュボード（締め状況）の閲覧', '承認の取り消し', PAID_LEAVE_COMPANY_WIDE]
  },
  MONTHLY_AGGREGATION_EXPORT: {
    name: '月次集計のPDF出力',
    group: 'insight',
    operations: ['月次集計・プロジェクト集計・勤務表一覧・通勤費精算一覧・経費精算一覧の PDF 出力']
  },
  MANAGEMENT_INSIGHT_VIEW: {
    name: '経営分析・収益予測の閲覧',
    group: 'insight',
    operations: ['ダッシュボード（全体状況）の閲覧', PAID_LEAVE_COMPANY_WIDE]
  },
  WORKFORCE_VIEW: { name: '稼働状況分析の閲覧', group: 'insight' },
  PROJECT_EDIT: {
    name: 'プロジェクトの登録・変更',
    group: 'business',
    operations: ['プロジェクトの登録・更新', '自分のプロジェクト所属の付け外し']
  },
  PROJECT_MANAGE: {
    name: 'プロジェクトの削除・詳細設定',
    group: 'business',
    operations: ['プロジェクトの削除・復元', '他社員のプロジェクト所属の変更', PAID_LEAVE_COMPANY_WIDE]
  },
  CLIENT_MANAGE: {
    name: '取引先情報の管理',
    group: 'business',
    // 全社の有休取得計画の閲覧は付けない（2026-09-27）。一般社員がプロジェクトと取引先を
    // 登録できるように配る権限で、全社を見せる権限ではない（back の全社表示の条件からも外した）。
    operations: ['取引先の登録・更新・削除']
  },
  SALES_MANAGE: {
    name: '売上情報の管理',
    group: 'business',
    // 請求書管理の［登録］［更新］［最新を取り込んで再作成］と、新規登録・更新の入力の表は、
    // この権限の人にだけ出す（back の経路表 /sales/register・update・recreate。
    // accessPolicy.js の canEditSalesRecords）。
    operations: ['請求書の作成・更新・再作成', PAID_LEAVE_COMPANY_WIDE]
  },
  INVOICE_MANAGE: {
    name: '請求書の管理',
    group: 'business',
    // 発行（請求番号と発行日を決める操作）は、PDF・ZIP の出力と同じ権限で通る
    // （back の経路表 /sales/invoice/）。請求書管理の［請求書］［一括ダウンロード］［発行］
    // ［一括発行］は、この権限の人にだけ出す（accessPolicy.js の canOutputAndIssueSalesInvoice）。
    operations: ['請求書の PDF・ZIP 出力', '請求書の発行', PAID_LEAVE_COMPANY_WIDE]
  },
  USER_MANAGE: {
    name: '社員・部署の管理',
    group: 'organization',
    operations: ['社員の削除・復元', '部署の登録・更新・削除', PAID_LEAVE_COMPANY_WIDE]
  },
  DEPARTMENT_VIEW: { name: '部署一覧の閲覧', group: 'organization', operations: ['部署一覧の閲覧（登録・変更不可）'] },
  ROLE_MANAGE: {
    name: '権限グループの作成・変更',
    group: 'organization',
    operations: [
      'ユーザー管理での社員登録・更新（権限グループ選択）',
      '権限管理での権限グループの作成・変更（社内システム管理者は「利用機能設定の確認」も必要）'
    ]
  },
  SYSTEM_SETTING_MANAGE: { name: '自社基本情報の管理', group: 'organization', operations: ['各種設定の保存・データの取り込み'] },
  ROLE_VIEW: {
    name: '権限グループ設定の確認',
    group: 'systemAdminOnly',
    onlyForRoles: ['TENANT_SYSTEM_ADMIN']
  },
  PERMISSION_VIEW: {
    name: '利用機能設定の確認',
    group: 'systemAdminOnly',
    onlyForRoles: ['TENANT_SYSTEM_ADMIN'],
    operations: ['権限管理での権限グループの作成・変更（「権限グループの作成・変更」も有効なとき）']
  },
  ROLE_ASSIGN: {
    name: '社員への権限グループ割り当て・解除',
    group: 'systemAdminOnly',
    onlyForRoles: ['TENANT_SYSTEM_ADMIN'],
    operations: ['権限管理の画面での割り当て・解除']
  },
  AUDIT_LOG_VIEW: { name: '操作履歴の閲覧（システム専用）', group: 'noEffect', noEffect: true },
  CONTRACT_MANAGE: { name: '契約情報の管理（準備中）', group: 'noEffect', noEffect: true }
}

/** 権限の名前の下に、効くメニューを並べる行の頭に付ける言葉（Gemini と決めた） */
export const MENUS_LABEL = '対象メニュー・機能：'

/** 経路表で役割から決まる画面の、権限による条件（custom の判定の中身を言葉にしたもの）。 */
const ROLE_BOUND_MENUS = [
  {
    to: '/admin/tenant-access-management',
    name: '権限管理',
    permissionsAll: ['ROLE_VIEW', 'PERMISSION_VIEW'],
    roles: ['TENANT_SYSTEM_ADMIN']
  },
  {
    to: '/admin/mfa-recovery',
    name: '2要素認証リセット',
    permissionsAll: ['ROLE_VIEW', 'PERMISSION_VIEW'],
    roles: ['TENANT_SYSTEM_ADMIN']
  }
]

function menuItemsOfNav () {
  const items = []
  const root = nav[0]
  ;((root && root._children) || []).forEach(item => {
    if (item && item._name === 'CSidebarNavItem' && item.to) items.push({ name: item.name, to: item.to })
  })
  return items
}

/**
 * この権限が「出る条件」に入っているメニュー。
 * @returns [{ name: 'ユーザー管理', with: ['ROLE_MANAGE'] }]  with は「両方オンのとき」の相手
 */
export function menusGovernedBy (code) {
  const result = []
  menuItemsOfNav().forEach(item => {
    const policy = accessPolicyForPath(item.to)
    if (!policy || typeof policy.custom === 'function') return
    const any = policy.permissionsAny || []
    const all = policy.permissionsAll || []
    if (any.includes(code)) result.push({ name: item.name, with: [] })
    else if (all.includes(code)) result.push({ name: item.name, with: all.filter(other => other !== code) })
  })
  ROLE_BOUND_MENUS.forEach(menu => {
    if (menu.permissionsAll.includes(code)) {
      result.push({ name: menu.name, with: menu.permissionsAll.filter(other => other !== code) })
    }
  })
  return result
}

/** 画面に出す「対象メニュー・機能」の文字（メニュー名と、画面の中の操作） */
export function effectText (code) {
  const guide = PERMISSION_GUIDE[code] || {}
  const menus = menusGovernedBy(code).map(menu => {
    if (!menu.with.length) return menu.name
    const partners = menu.with.map(other => '「' + permissionName(other) + '」').join('')
    return menu.name + '（' + partners + 'の有効化が必要）'
  })
  return menus.concat(guide.operations || []).join('、')
}

export function permissionName (code) {
  const guide = PERMISSION_GUIDE[code]
  return guide ? guide.name : ''
}

/**
 * 左メニューの項目ごとに、出すのに要る権限を並べた一覧（画面の「メニューの表示に必要な権限一覧」）。
 *
 * 「ユーザー管理はどの権限で使えるのか分からない」への答え。**権限から引く向き（effectText）
 * だけでは、メニューから引けない。**両方の向きを同じ経路表から作る。
 *
 * @returns [{ group, name, kind, permissions, note }]
 *   kind：'any'（いずれか1つ）/ 'all'（全部）/ 'everyone'（権限の設定は不要）/ 'role'（権限グループで決まる）
 */
export function menuPermissionTable () {
  const rows = []
  let group = ''
  const root = nav[0]
  ;((root && root._children) || []).forEach(item => {
    if (!item) return
    if (item._name === 'CSidebarNavTitle') {
      group = Array.isArray(item._children) ? String(item._children[0] || '') : ''
      return
    }
    if (item._name !== 'CSidebarNavItem' || !item.to) return
    const policy = accessPolicyForPath(item.to)
    const all = (policy && policy.permissionsAll) || []
    const any = (policy && policy.permissionsAny) || []
    if (all.length) rows.push({ group, name: item.name, kind: 'all', permissions: all.map(permissionName), note: '' })
    else if (any.length) rows.push({ group, name: item.name, kind: 'any', permissions: any.map(permissionName), note: '' })
    else rows.push({ group, name: item.name, kind: 'everyone', permissions: [], note: '' })
  })
  ROLE_BOUND_MENUS.forEach(menu => {
    rows.push({
      group: '自社の権限',
      name: menu.name,
      kind: 'role',
      permissions: menu.permissionsAll.map(permissionName),
      note: '契約企業管理者（自社最上位）には常に表示されます。'
    })
  })
  rows.push({
    group: 'ご請求',
    name: '請求・お支払い',
    kind: 'role',
    permissions: [],
    note: '「契約企業管理者（自社最上位）」および「社内システム管理者」に表示されます（権限設定による変更はできません）。'
  })
  return rows
}

/**
 * この権限グループの人に出る左メニュー。**左メニューと同じ関数で計算する。**
 * @param roleCode 権限グループのコード（TENANT_SYSTEM_ADMIN など。独自のものは独自のコード）
 * @param permissionCodes 権限グループに入っている権限（会社で使えるものだけ）
 * @param features 会社で使える機能
 */
export function menusForGroup (roleCode, permissionCodes, features) {
  return sidebarMenuItems({
    roles: roleCode ? [roleCode] : [],
    features: features || [],
    permissions: permissionCodes || []
  })
}
